import { motion, AnimatePresence } from 'framer-motion'
import { Search, FileText, Database, Wrench, Terminal, BarChartHorizontal, MessageSquare, Puzzle, Globe, BrainCircuit } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { tokens } from '@/locales/translations/tokens'
import { ToolGroup } from '@/features/workstation/hooks/useReasoningTools'
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip'

interface ReasoningResultsProps {
  toolGroups: ToolGroup[]
}

function getToolIcon(toolName: string) {
  const icons: Record<string, React.ElementType> = {
    
    plannerTool: BrainCircuit,
    userFilesTool: FileText,
    pageSearchTool: Search,
    documentSearchTool: FileText,
    unifiedSearchTool: FileText,
    databaseTool: Database,
    chatHistoryTool: MessageSquare,
    queryGenerationTool: Puzzle,
    externalSearchTool: Globe,
    terminalTool: Terminal,
    analyticsTool: BarChartHorizontal
  }
  
  for (const [key, icon] of Object.entries(icons)) {
    if (toolName.toLowerCase().includes(key.toLowerCase())) {
      return icon
    }
  }
  
  return Wrench
}

function getToolTranslationKey(toolName: string): string {
  const toolTranslations: Record<string, string> = {
    plannerTool: 'workstation.reasoning.tools.planner',
    userFilesTool: 'workstation.reasoning.tools.userFiles',
    pageSearchTool: 'workstation.reasoning.tools.pageSearch',
    documentSearchTool: 'workstation.reasoning.tools.documentSearch',
    videoSearchTool: 'workstation.reasoning.tools.videoSearch',
    userFoldersTool: 'workstation.reasoning.tools.userFolders',
    contentGeneratorTool: 'workstation.reasoning.tools.contentGenerator',
    queryGenerationTool: 'workstation.reasoning.tools.queryGeneration',
    databaseTool: 'workstation.reasoning.tools.database',
    externalSearchTool: 'workstation.reasoning.tools.externalSearchTool',
    chatHistoryTool: 'workstation.reasoning.tools.chatHistory',
    terminalTool: 'workstation.reasoning.tools.terminal',
    analyticsTool: 'workstation.reasoning.tools.analytics',
    unifiedSearchTool: 'workstation.reasoning.tools.unifiedSearch',
  }
  
  for (const [key, translationKey] of Object.entries(toolTranslations)) {
    if (toolName.toLowerCase().includes(key.toLowerCase())) {
      return translationKey
    }
  }
  
  return 'workstation.reasoning.tools.generic'
}

export const ReasoningResults = ({ toolGroups }: ReasoningResultsProps) => {
  const { t } = useTranslation()
  
  if (!toolGroups || toolGroups.length === 0) {
    return (
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        className='p-8 text-center'
      >
          <p className='text-sm text-muted-foreground'>
            {t(tokens.workstation.sources.empty)} 
          </p>
      </motion.div>
    )
  }

  return (
    <div className='space-y-3'>
      <AnimatePresence mode='popLayout' initial={false}>
        {toolGroups.map((group, groupIndex) => (
          <motion.div
            key={`group-${group.timestamp}-${groupIndex}`}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, height: 0, overflow: 'hidden' }}
            transition={{ delay: groupIndex * 0.02, duration: 0.2 }}
            className='p-4'
          >
            <div className='mb-2 flex items-center justify-between'>
              <h3 className='text-sm font-medium text-foreground'>
                {t(tokens.tokens.workstation.reasoning.process_steps)}
              </h3>
              <span className='text-xs text-muted-foreground'>
                {new Date(group.timestamp).toLocaleTimeString()}
              </span>
            </div>

            <div className='space-y-2'>
              {group.tools.map((tool, index) => {
                const IconComponent = getToolIcon(tool.toolName)
                const translationKey = getToolTranslationKey(tool.toolName)
                const toolDisplayName = t(translationKey, tool.toolName)
                
                return (
                  <motion.div
                    key={`${tool.id}-${index}`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    exit={{ opacity: 0, height: 0 }}
                    transition={{ delay: index * 0.03, duration: 0.2 }}
                  >
                    <div className='rounded-xl border shadow-sm p-3 bg-card/50 hover:bg-card/80 transition-colors'>
                      <div className='flex items-center gap-2 truncate'>
                        <IconComponent className='h-4 w-4 text-primary/70' />
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <span className='text-sm font-medium truncate'>{toolDisplayName}</span>
                            </TooltipTrigger>
                            <TooltipContent>
                              <p>{toolDisplayName}</p>
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      </div>
                    </div>
                  </motion.div>
                )
              })}
            </div>
          </motion.div>
        ))}
      </AnimatePresence>
    </div>
  );
} 